import { api } from "../api";

import { PaginationOptions, PaginatedResponse } from "@/types/Pagination";
import { MyNewKennelRegistrationRequest } from "@/types/portal/Workflow";
import CommandResult from "@/types/CommandResult";
import { withoutActorIdentity } from "./actorIdentity";
import { getErrorResponse } from "../errorHandler";

type WorkflowPaymentRequest = {
    paymentInfo: object | null;
    workflowGuid: string;
};

enum WorkflowPath {    
    DogRegistration = '/Dog',
    DogRegistrationIndividual = '/DogRegistration',
    DogRegistrationIndividualKennelNameUsage = '/DogRegistration/kennels',
    QTRegistration = '/Dog/qt',
    KennelRegistration = '/Kennel',
    KennelChangeOwners = '/Kennel/owners',
    LitterRegistration = '/Litter',
    LeaseRegistration = '/Lease',
    AffiliateRegistration = '/Affiliate/registration',
    AffiliateRenewal = '/Affiliate/renewal',
}

const getWorkflowFees = async (path: string, id?: string | number): Promise<any> => {
    let fullPath = `${path}/fees`;

    if (!!id) fullPath += `/${id}`;

    const { data } = await api.get<any>(fullPath);
    return data;
}

const getWorkflowsList = async (
    options: PaginationOptions,
    path: WorkflowPath
): Promise<PaginatedResponse<Object>> => {
    const { page, itemsPerPage, sortBy, sortDesc } = options;

    const sort = !!sortBy?.length ? sortBy[0] : 'requestId';
    const asce = !!sortDesc?.length ? !sortDesc[0] : true;

    const { data } = await api.get<PaginatedResponse<Object>>(`${path}/requests`, {
        params: { pageSize: itemsPerPage, page, ascending: asce, sort }
    });

    return data;
}

const getWorkflowRequestDetails = async (workflowGuid: string, path: WorkflowPath): Promise<any> => {  
    const { data } = await api.get<any>(`${path}/myrequest/${workflowGuid}`);
    return data;
};

const getSignatureRequestList = async (
    options: PaginationOptions,
    path: WorkflowPath
): Promise<PaginatedResponse<Object>> => {
    const { page, itemsPerPage, sortBy, sortDesc } = options;

    const sort = !!sortBy?.length ? sortBy[0] : 'requestId';
    const asce = !!sortDesc?.length ? !sortDesc[0] : true;

    const { data } = await api.get<PaginatedResponse<Object>>(`${path}/signatures`, {
        params: { pageSize: itemsPerPage, page, ascending: asce, sort }
    });

    return data;
}

const getWorkflowDetailsForSignature = async (signatureId: number, path: WorkflowPath): Promise<any> => {
    const { data } = await api.get<any>(`${path}/signature/${signatureId}`);
    return data;
}

const signWorkflow = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String,
    path: WorkflowPath,
): Promise<any> => {
    const { data } = await api.post<object, { data: CommandResult }>(`${path}/signature`, {
        workflowGuid,
        signatureId,
        signatureText
    });

    if (!data.success) throw new Error(data.errorMessage);
    return data;
}

const submitWorkflowCCAuthorization = async (
    request: WorkflowPaymentRequest,
    path: WorkflowPath
): Promise<any> => {
    try {
        const { data } = await api.post<object>(`${path}/paymentauth`, withoutActorIdentity(request));
        return data;
    } catch (err: any) {
        return getErrorResponse(err)?.data;
    }
}

const submitWorkflowCCPayment = async (
    request: WorkflowPaymentRequest,
    path: WorkflowPath
  ): Promise<any> => {
    try {
      const { data } = await api.post<object>(`${path}/payment`, withoutActorIdentity(request));
      return data;
    } catch (err: any) {
      return getErrorResponse(err)?.data;
    }
};

// ---- FEES ----

export const getRegisterQTFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.QTRegistration}/registration`);

export const getRegisterKennelFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.KennelRegistration}/registration`);

export const getChangeKennelOwnerFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.KennelChangeOwners}/registration`);

export const getRegisterLitterFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.LitterRegistration}/registration`);

export const getRegisterLeaseFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.LeaseRegistration}/registration`);

export const getLeaseFeeForPerson = async (): Promise<any> =>
    (await api.get<any>(`${WorkflowPath.LeaseRegistration}/registration/fee`)).data;

export const getRegisterAffiliateFees = async (): Promise<any> =>
    await getWorkflowFees(WorkflowPath.AffiliateRegistration);

export const getRenewAffiliateFees = async (affiliateId: string): Promise<any> =>
    await getWorkflowFees(WorkflowPath.AffiliateRenewal, affiliateId);

export const getDogRegistrationIndividualFees = async (): Promise<any> =>
    await getWorkflowFees(`${WorkflowPath.DogRegistrationIndividual}/registration`);

// ---- LIST ----

export const getQTDogRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.QTRegistration);

export const getDogRegistrationIndividualRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.DogRegistrationIndividual);

export const getKennelRegistrationRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.KennelRegistration);

export const getChangeKennelOwnerRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.KennelChangeOwners);

export const getLitterRegistrationRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.LitterRegistration);

export const getLeaseRegistrationRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.LeaseRegistration);

export const getAffiliateRegistrationRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.AffiliateRegistration);

export const getAffiliateRenewalRequests = async (options: PaginationOptions): Promise<any> =>
    await getWorkflowsList(options, WorkflowPath.AffiliateRenewal);

// ---- DETAILS ----

export const getQTDogRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.QTRegistration);

export const getDogRegistrationIndividualRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.DogRegistrationIndividual);

export const getKennelRequestDetails = async (workflowGuid: string): Promise<MyNewKennelRegistrationRequest> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.KennelRegistration);

export const getChangeKennelOwnerRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.KennelChangeOwners);

export const getLitterRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.LitterRegistration);

export const getLeaseRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.LeaseRegistration);

export const getAffiliateRegistrationRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.AffiliateRegistration);

export const getAffiliateRenewalRequestDetails = async (workflowGuid: string): Promise<any> =>
    await getWorkflowRequestDetails(workflowGuid, WorkflowPath.AffiliateRenewal);

// ---- SIGNATURE LIST ----

export const getQTDogSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.QTRegistration);

export const getDogRegistrationIndividualRegistrationSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.DogRegistrationIndividual);

export const getDogRegistrationIndividualKennelNameUsageSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.DogRegistrationIndividualKennelNameUsage);

export const getKennelRegistrationSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.KennelRegistration);

export const getChangeKennelOwnerSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.KennelChangeOwners);

export const getLitterRegistrationSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.LitterRegistration);

export const getLeaseRegistrationSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.LeaseRegistration);

export const getAffiliateRegistrationSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.AffiliateRegistration);

export const getAffiliateRenewalSignatureRequests = async (options: PaginationOptions): Promise<any> =>
    await getSignatureRequestList(options, WorkflowPath.AffiliateRenewal);

// ---- SIGNATURE DETAILS ----

export const getQTDogDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.QTRegistration);

export const getDogRegistrationIndividualRegistrationSignatureRequest = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.DogRegistrationIndividual);

export const getDogRegistrationIndividualKennelNameUsageSignatureRequest = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.DogRegistrationIndividualKennelNameUsage);

export const getKennelRegistrationDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.KennelRegistration);

export const getChangeKennelOwnerRequestDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.KennelChangeOwners);

export const getLitterRegistrationDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.LitterRegistration);

export const getLeaseDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.LeaseRegistration);

export const getAffiliateRegistrationDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.AffiliateRegistration);

export const getAffiliateRenewalDetailsForSignature = async (signatureId: number): Promise<any> =>
    await getWorkflowDetailsForSignature(signatureId, WorkflowPath.AffiliateRenewal);

// ---- SIGN SIGNATURE ----

export const signDog = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.DogRegistration);

export const signDogRegistrationIndividual = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.DogRegistrationIndividual);

export const signDogRegistrationIndividualKennelNameUsage = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.DogRegistrationIndividualKennelNameUsage);

export const signKennelRegistration = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.KennelRegistration);

export const signChangeKennelOwnerRequest = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.KennelChangeOwners);

export const signLitterRegistration = async (
  workflowGuid: String,
  signatureId: number,
  signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.LitterRegistration);

export const signLease = async (
  workflowGuid: String,
  signatureId: number,
  signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.LeaseRegistration);

export const signAffiliateRegistration = async (
    workflowGuid: String,
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.AffiliateRegistration);

export const signAffiliateRenewal = async (
    workflowGuid: String, 
    signatureId: number,
    signatureText: String
): Promise<any> => await signWorkflow(workflowGuid, signatureId, signatureText, WorkflowPath.AffiliateRenewal);

// ---- PAYMENT AUTHORIZATION ----

export const submitAffiliateRenewalCCAuthorization = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCAuthorization(request, WorkflowPath.AffiliateRenewal);
    
export const submitAffiliateRegistrationCCAuthorization = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCAuthorization(request, WorkflowPath.AffiliateRegistration);

// ---- PAYMENT ----

export const submitDogCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.DogRegistration);

export const submitDogRegistrationIndividualCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.DogRegistrationIndividual);

export const submitKennelCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.KennelRegistration)

export const submitKennelOwnerCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.KennelChangeOwners);

export const submitLitterCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.LitterRegistration);

export const submitLeaseCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.LeaseRegistration);

export const submitAffiliateRegistrationCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.AffiliateRegistration);

export const submitAffiliateRenewalCCPayment = async (request: WorkflowPaymentRequest): Promise<any> =>
    await submitWorkflowCCPayment(request, WorkflowPath.AffiliateRenewal);

export default {
    // ---- FEES ----
    getRegisterQTFees,
    getRegisterKennelFees,
    getChangeKennelOwnerFees,
    getRegisterLitterFees,
    getRegisterLeaseFees,
    getLeaseFeeForPerson,
    getRegisterAffiliateFees,
    getRenewAffiliateFees,
    getDogRegistrationIndividualFees,
    // ---- LIST ----
    getQTDogRequests,
    getDogRegistrationIndividualRequests,
    getKennelRegistrationRequests,
    getChangeKennelOwnerRequests,
    getLitterRegistrationRequests,
    getLeaseRegistrationRequests,
    getAffiliateRegistrationRequests,
    getAffiliateRenewalRequests,
    // ---- DETAILS ----
    getQTDogRequestDetails,
    getDogRegistrationIndividualRequestDetails,
    getKennelRequestDetails,
    getChangeKennelOwnerRequestDetails,
    getLitterRequestDetails,
    getLeaseRequestDetails,
    getAffiliateRegistrationRequestDetails,
    getAffiliateRenewalRequestDetails,
    // ---- SIGNATURE LIST ----
    getQTDogSignatureRequests,
    getDogRegistrationIndividualRegistrationSignatureRequests,
    getDogRegistrationIndividualKennelNameUsageSignatureRequests,
    getKennelRegistrationSignatureRequests,
    getChangeKennelOwnerSignatureRequests,
    getLitterRegistrationSignatureRequests,
    getLeaseRegistrationSignatureRequests,
    getAffiliateRegistrationSignatureRequests,
    getAffiliateRenewalSignatureRequests,
    // ---- SIGNATURE DETAILS ----
    getQTDogDetailsForSignature,
    getDogRegistrationIndividualRegistrationSignatureRequest,
    getDogRegistrationIndividualKennelNameUsageSignatureRequest,
    getKennelRegistrationDetailsForSignature,
    getChangeKennelOwnerRequestDetailsForSignature,
    getLitterRegistrationDetailsForSignature,
    getLeaseDetailsForSignature,
    getAffiliateRegistrationDetailsForSignature,
    getAffiliateRenewalDetailsForSignature,
    // ---- SIGN SIGNATURE ----
    signDog,
    signDogRegistrationIndividual,
    signDogRegistrationIndividualKennelNameUsage,
    signKennelRegistration,
    signChangeKennelOwnerRequest,
    signLitterRegistration,
    signLease,
    signAffiliateRegistration,
    signAffiliateRenewal,
    // ---- AUTHORIZE PAYMENT ----
    submitAffiliateRenewalCCAuthorization,
    submitAffiliateRegistrationCCAuthorization,
    // ---- PAYMENT ----
    submitDogCCPayment,
    submitDogRegistrationIndividualCCPayment,
    submitKennelCCPayment,
    submitKennelOwnerCCPayment,
    submitLitterCCPayment,
    submitLeaseCCPayment,
    submitAffiliateRegistrationCCPayment,
    submitAffiliateRenewalCCPayment
}
